import {
  get,
  post,
  put,
  getDrawProgressData,
  getBudgetReconcileData
} from "../../network/apiService";
import { apiUrlHost } from "../servicerOnboarding/servicerOnboarding.action";
export const getLoanInfoForPL = (loanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/aggregate/loanInfo?toorakLoanId=${loanId}`;
  return get(url);
};

export const updatePurchaseFlags = (requestBody: any) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/flag`;
  return put(url, requestBody);
};

export const updateDrawDocsTags = (documentId: string, requestBody: any[]) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/${documentId}/tags`;
  return put(url, requestBody);
};

export const updateDrawDocsAssets = (requestBody: any[], documentId: any) => {
  let url = `${apiUrlHost}/drawservice/assetmanagement/document/assets`;
  if (!requestBody || requestBody.length === 0) {
    url += `?documentId=${documentId}`;
  }
  return put(url, requestBody);
};

export const updateDocsReviewStatus = (requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/updateStatus`;
  return post(url, requestBody);
};

export const transferDocs = (documentId: string, requestBody: any) => {
  const url = `${apiUrlHost}/documentdetails/${documentId}/transfer`;
  return put(url, requestBody);
};

export const importDocs = (toorakLoanId: string, requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/import`;
  return put(url, requestBody);
};

export const fetchDrawProgress = async (
  toorakLoanId: string,
  requiredDraw: any
) => {
  try {
    // Get all draws sorted by drawNumber
    const allDraws =
      requiredDraw?.allDraws?.filter(
        (draw: any) =>
          draw?.drawNumber !== undefined && draw?.drawNumber !== null
      ) || [];

    if (allDraws.length === 0) {
      return Promise.resolve({ lineItems: [], draws: [] });
    }

    // Sort draws by drawNumber
    allDraws.sort(
      (a: any, b: any) => Number(a.drawNumber) - Number(b.drawNumber)
    );

    // Separate qualifying and non-qualifying draws
    const qualifyingDraws = allDraws.filter(
      (draw: any) =>
        draw?.budgetReconcileStatus === "Completed" &&
        (draw?.inspectionReviewStatus === "Approved" ||
          draw?.inspectionReviewStatus === "Approved Conditionally")
    );

    // Call budget reconcile endpoint only for qualifying draws
    const drawDataPromises = qualifyingDraws.map((draw: any) =>
      getBudgetReconcileData(draw.drawId).catch((err: any) => {
        console.error(
          `Error fetching budget reconcile for draw ${draw.drawId}:`,
          err
        );
        return { data: [] };
      })
    );

    const qualifyingDrawDataResponses = await Promise.all(drawDataPromises);

    // Create a map of draw data for quick lookup
    const drawDataMap = new Map<string, any>();
    qualifyingDraws.forEach((draw: any, idx: number) => {
      const drawId = draw.drawId.toString();
      drawDataMap.set(drawId, qualifyingDrawDataResponses[idx]?.data || []);
    });

    // Collect all unique line items across all qualifying draws that have data
    const lineItemsMap = new Map<number, any>();
    const processedDraws: any[] = [];

    // First pass: collect all unique line items from qualifying draws with data
    qualifyingDraws.forEach((draw: any) => {
      const drawId = draw.drawId.toString();
      const reconciledItems = drawDataMap.get(drawId) || [];

      reconciledItems.forEach((item: any) => {
        const lineItemId = item.budgetLineItemsId;
        if (!lineItemsMap.has(lineItemId)) {
          lineItemsMap.set(lineItemId, {
            item: item.budgetLineItem?.item || "",
            description: item.budgetLineItem?.description || "",
            desc: item.budgetLineItem?.description || "",
            budget: item.budgetLineItem?.budget || "",
            financedBudget: item.budgetLineItem?.financedBudget || "",
            inspectionInfo: {}
          });
        }
      });
    });

    // Second pass: process all draws (both qualifying and non-qualifying)
    allDraws.forEach((draw: any) => {
      const drawId = draw.drawId.toString();
      const isQualifying = qualifyingDraws.some(
        (d: any) => d.drawId === draw.drawId
      );
      const reconciledItems = isQualifying ? drawDataMap.get(drawId) || [] : [];
      const hasData = reconciledItems.length > 0;

      processedDraws.push({
        drawId,
        lineItems: Array.from(lineItemsMap.values()).map((lineItem: any) => {
          // Find matching item in reconciled items for this draw
          const matchingItem = reconciledItems.find(
            (item: any) =>
              item.budgetLineItemsId ===
              [...lineItemsMap.entries()].find(([_, v]) => v === lineItem)?.[0]
          );

          if (!isQualifying || !hasData || !matchingItem) {
            // For non-qualifying draws or qualifying draws with no data, show "-"
            return {
              item: lineItem.item,
              desc: lineItem.desc,
              amount: "-",
              completion: "-"
            };
          }

          // For qualifying draws with data, calculate the approved draw amount
          const financedBudget =
            Number(matchingItem.budgetLineItem?.financedBudget) || 0;
          const approvedLatestCompletion =
            Number(matchingItem.approvedLatestCompletion) || 0;
          const previousCompletion =
            Number(matchingItem.previousCompletion) || 0;

          // Calculate approved draw amount using the formula:
          const approvedDrawAmount = (
            (financedBudget * (approvedLatestCompletion - previousCompletion)) /
            100
          ).toFixed(2);

          return {
            item: lineItem.item,
            desc: lineItem.desc,
            amount: approvedDrawAmount,
            completion: approvedLatestCompletion.toFixed(2)
          };
        })
      });

      // Update inspection info for line items
      reconciledItems.forEach((item: any) => {
        const lineItemId = item.budgetLineItemsId;
        if (lineItemsMap.has(lineItemId)) {
          const lineItem = lineItemsMap.get(lineItemId)!;
          lineItem.inspectionInfo[drawId] = {
            latestCompletion:
              isQualifying && hasData ? item.approvedLatestCompletion : "-",
            latestInspectionBudget: item.budgetLineItem?.financedBudget,
            previousCompletion: item.previousCompletion
          };
        }
      });
    });

    const lineItems = Array.from(lineItemsMap.values());

    return Promise.resolve({
      lineItems,
      draws: processedDraws
    });
  } catch (err) {
    console.error(err);
    return Promise.resolve({ lineItems: [], draws: [] });
  }
};

export const startTaggingDocs = (toorakLoanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/tag`;
  return put(url);
};
